/**
 * Pinia-стор корзины.
 * Сохраняет поведение Vuex-модуля cartModule (actions + mutations).
 */
import { defineStore } from 'pinia'
import { api } from '@/api'
import { apiPost } from '@/api/client'
import { activeCondimentsEqual, modsEqual } from '@/services/cart'
import type { CartItem, ComboCartGroup, OrderRequest, OrderResponse, PromoResult } from '@/types'

function getGroupsHash(groups: ComboCartGroup[] | undefined | null): string {
  if (!groups) { return ''; }
  return groups.map(g => {
    const pid = g.product_id;
    const mods = (g.product_mods || [])
      .flatMap(m => (m.items || []).map(i => i.id))
      .sort().join(',');
    const condiments = (g.product_condiments || [])
      .map(c => c.id).sort().join(',');
    return `G${g.id}:P${pid}[m:${mods}][c:${condiments}]`;
  }).sort().join('|');
}

function findNonComboItem(cart: CartItem[], product: CartItem): CartItem | undefined {
  return cart.find(item =>
    item.id === product.id &&
    (
      product.custom_fast_set === true
        ? Array.isArray(item.products) && Array.isArray(product.products) &&
          item.products.map(p => p.id).join(',') === product.products.map(p => p.id).join(',')
        : product.fast_set === true
          ? item.modifier === product.modifier
          : true
    ) &&
    (
      Array.isArray(product.active_condiments)
        ? activeCondimentsEqual(item.active_condiments, product.active_condiments)
        : !item.active_condiments || !item.active_condiments.length
    ) &&
    (
      Array.isArray(product.mods)
        ? modsEqual(item.mods, product.mods)
        : !item.mods || !item.mods.length
    )
  );
}

export const useCartStore = defineStore('cart', {
  state: () => ({
    cart: [] as CartItem[],
    editableCombo: null as unknown | null,
    promocode: null as string | null,
    promoObj: null as unknown | null,
    order: null as unknown | null,
  }),

  actions: {
    setCart(data: CartItem[]) {
      this.cart = data;
    },

    setPromocode(promocode: string | null) {
      this.promocode = promocode
    },

    setPromoObj(promoObj: unknown | null) {
      this.promoObj = promoObj
    },

    setOrder(order: unknown | null) {
      this.order = order
    },

    setEditableCombo(combo: unknown | null) {
      this.editableCombo = combo
    },

    productDecrease(product: CartItem) {
      const cart = JSON.parse(JSON.stringify(this.cart)) as CartItem[];
      let elem: CartItem | undefined;

      if (product.cartKey) {
        elem = cart.find(item => item.cartKey === product.cartKey);
      }

      if (!elem) {
        if (!product.is_combo) {
          elem = findNonComboItem(cart, product);
        } else {
          const targetHash = getGroupsHash(product.groups);
          elem = cart.find(item => {
            if (!item.is_combo || item.id !== product.id) { return false; }
            return getGroupsHash(item.groups) === targetHash;
          });
        }
      }

      if (elem) {
        if (elem.quantity > 1) {
          elem.quantity -= 1;
        } else {
          const index = cart.findIndex(item =>
            item.cartKey ? item.cartKey === elem!.cartKey : item === elem
          );
          if (index > -1) {
            cart.splice(index, 1);
          }
        }

        this.setCart(cart);
      }
    },

    productIncrease(product: CartItem) {
      const cart = JSON.parse(JSON.stringify(this.cart)) as CartItem[];
      const isEditing = !!product.oldItemKey;
      const explicitQuantity = !!product.explicitQuantity;

      if (isEditing) {
        const oldIndex = cart.findIndex(item => item.cartKey === product.oldItemKey);
        if (oldIndex !== -1) {
          cart.splice(oldIndex, 1);
        }
        delete product.oldItemKey;
      }

      if (!product.is_combo) {
        const elem = findNonComboItem(cart, product);

        if (elem) {
          if (isEditing || explicitQuantity) {
            elem.quantity = product.quantity || 1;
          } else {
            elem.quantity += 1;
          }
        } else {
          const itemToPush = {
            ...product,
            quantity: product.quantity || 1,
            cartKey: Date.now() + Math.random().toString(36).substr(2, 9)
          } as CartItem;

          if (Array.isArray(product.mods) && product.mods.length) {
            itemToPush.mods = JSON.parse(JSON.stringify(product.mods));
          } else if (!product.mods) {
            itemToPush.mods = [];
          }

          if (Array.isArray(product.active_condiments) && product.active_condiments.length) {
            itemToPush.active_condiments = JSON.parse(JSON.stringify(product.active_condiments));
            itemToPush.condiments = JSON.parse(JSON.stringify(product.active_condiments));
          } else {
            itemToPush.condiments = [];
            itemToPush.active_condiments = [];
          }

          cart.push(itemToPush);
        }
      } else {
        const comboQty = product.quantity || 1;

        const comboElem = cart.find(item => {
          if (!item.is_combo || item.id !== product.id) { return false; }
          return getGroupsHash(product.groups) === getGroupsHash(item.groups);
        });

        if (comboElem) {
          if (isEditing || explicitQuantity) {
            comboElem.quantity = comboQty;
          } else {
            comboElem.quantity += 1;
          }
        } else {
          cart.push({
            ...product,
            quantity: comboQty,
            cartKey: `combo_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`
          } as CartItem);
        }
      }

      this.setCart(cart);
    },

    productAddToCart(product: CartItem) {
      const cart = [...this.cart];

      const itemToPush = {
        ...product,
        cartKey: Date.now() + Math.random().toString(36).substr(2, 9)
      } as CartItem;

      if (Array.isArray(product.mods) && product.mods.length) {
        itemToPush.mods = JSON.parse(JSON.stringify(product.mods));
      } else if (!product.mods) {
        itemToPush.mods = [];
      }

      if (Array.isArray(product.active_condiments) && product.active_condiments.length) {
        itemToPush.active_condiments = JSON.parse(JSON.stringify(product.active_condiments));
        itemToPush.condiments = JSON.parse(JSON.stringify(product.active_condiments));
      } else {
        itemToPush.condiments = [];
        itemToPush.active_condiments = [];
      }

      cart.push(itemToPush);

      this.setCart(cart);
    },

    productDelete(product: CartItem) {
      const cart = JSON.parse(JSON.stringify(this.cart)) as CartItem[];
      let index = -1;

      if (product.cartKey) {
        index = cart.findIndex(item => item.cartKey === product.cartKey);
      }

      if (index === -1) {
        if (product.is_combo) {
          const targetHash = getGroupsHash(product.groups);
          index = cart.findIndex(item => {
            if (!item.is_combo || item.id !== product.id) { return false; }
            return getGroupsHash(item.groups) === targetHash;
          });
        } else {
          index = cart.findIndex(item =>
            item.id === product.id &&
            (
              product.custom_fast_set === true
                ? Array.isArray(item.products) && Array.isArray(product.products) &&
                  item.products.map(p => p.id).join(',') === product.products.map(p => p.id).join(',')
                : product.fast_set === true
                  ? item.modifier === product.modifier
                  : true
            ) &&
            (
              Array.isArray(product.active_condiments)
                ? activeCondimentsEqual(item.active_condiments, product.active_condiments)
                : !item.active_condiments || !item.active_condiments.length
            ) &&
            (
              Array.isArray(product.mods)
                ? modsEqual(item.mods, product.mods)
                : !item.mods || !item.mods.length
            )
          );
        }
      }

      if (index > -1) {
        cart.splice(index, 1);
        this.setCart(cart);
      }
    },

    async checkDiscount(cafeSlug: string | null, data: { promocode: string; cart: CartItem[] }) {
      const items = data.cart.filter(x => !x.is_promo && !x.fast_set);
      const fastSets = data.cart.filter(x => x.fast_set);

      const requestData: Record<string, unknown> = {
        discount_code: data.promocode
      };

      if (items.length > 0) {
        requestData.items = items;
      }

      if (fastSets.length > 0) {
        requestData.fast_sets = fastSets;
      }

      return apiPost<PromoResult>(`${api}/shops/${cafeSlug}/check-discount/`, requestData);
    },

    async sendOrder(cafeSlug: string | null, data: OrderRequest) {
      return apiPost<OrderResponse>(`${api}/orders/`, data, {
        meta: {
          title: 'Запрос на оформление заказа',
          page: 'CartView',
          func: 'sendOrder',
          kiosk: { cafe_slug: cafeSlug }
        }
      });
    },
  },
})